import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Banner, BannerName } from '@shared/models/banner.model';
import { Observable, map, of, shareReplay } from 'rxjs';
import { ConfigService } from './config.service';

type BannerData<Name extends string> = {
  [K in `${Name}-img` | `${Name}-url`]: string;
};

export type BannerResponse = [{
  applicationTitle: string;
  localContent: {
    [K in BannerName]: BannerData<K>;
  };
}];

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private readonly http = inject(HttpClient);

  public readonly bannerData$: Observable<Record<BannerName, Banner>> | undefined;

  public constructor() {
    const configService = inject(ConfigService);

    const bannerUrl = configService.config?.advertisingBannersUrl;
    if (bannerUrl?.length) {
      this.bannerData$ = this.http.get<BannerResponse>(bannerUrl).pipe(map(this.parseBannerData.bind(this)), shareReplay(1));
    }
  }

  public getBannerByName(bannerName: BannerName | undefined): Observable<Banner | undefined> {
    return this.bannerData$?.pipe(map(bannerData => bannerName ? bannerData[bannerName] : undefined)) ?? of(undefined);
  }

  private parseBannerData(raw: BannerResponse): Record<BannerName, Banner> {
    return Object.fromEntries(
      Object.entries(raw[0].localContent as Record<string, unknown>)
        .map(([k, v]) => [k as BannerName, this.toBanner(k as BannerName, v as BannerData<BannerName>)]),
    ) as Record<BannerName, Banner>;
  }

  private toBanner<Name extends BannerName>(name: Name, bannerData: BannerData<Name>): Banner {
    return {
      name,
      imageUrl: bannerData[`${name}-img`],
      link: bannerData[`${name}-url`],
    };
  }
}
